import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useData } from '../../context/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiEdit3, FiTrash2, FiSave, FiMessageCircle } = FiIcons;

const IncidentDetailModal = ({ incident, onClose }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(incident);
  const [newComment, setNewComment] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { updateIncident, deleteIncident } = useData();

  const handleSave = () => {
    updateIncident(incident.id, editData);
    setIsEditing(false);
  };

  const handleDelete = () => {
    deleteIncident(incident.id);
    onClose();
  };

  const handleAddComment = () => {
    if (newComment.trim()) {
      const updatedComments = [
        ...(incident.comments || []),
        {
          id: Date.now(),
          text: newComment.trim(),
          createdAt: new Date().toISOString(),
          author: 'Usuario'
        }
      ];
      updateIncident(incident.id, { comments: updatedComments });
      setNewComment('');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      baja: 'bg-green-100 text-green-800',
      media: 'bg-yellow-100 text-yellow-800',
      alta: 'bg-orange-100 text-orange-800',
      critica: 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      en_proceso: 'bg-blue-100 text-blue-800',
      resuelto: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">Detalle de Incidencia</h3>
            <div className="flex items-center space-x-2">
              {!isEditing && (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <SafeIcon icon={FiEdit3} />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Title and Status */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {isEditing ? (
                <input
                  type="text"
                  value={editData.title}
                  onChange={(e) => setEditData({...editData, title: e.target.value})}
                  className="w-full text-xl font-semibold text-gray-800 border-b border-gray-200 focus:outline-none focus:border-blue-500 pb-2"
                />
              ) : (
                <h2 className="text-xl font-semibold text-gray-800">{incident.title}</h2>
              )}
            </div>
            <div className="flex flex-col space-y-2 ml-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(incident.priority)}`}>
                {incident.priority}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(incident.status)}`}>
                {incident.status}
              </span>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
            {isEditing ? (
              <textarea
                value={editData.description}
                onChange={(e) => setEditData({...editData, description: e.target.value})}
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <p className="text-gray-600">{incident.description}</p>
            )}
          </div>

          {/* Details Grid */}
          {isEditing ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                <select
                  value={editData.status}
                  onChange={(e) => setEditData({...editData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="en_proceso">En Proceso</option>
                  <option value="resuelto">Resuelto</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prioridad</label>
                <select
                  value={editData.priority}
                  onChange={(e) => setEditData({...editData, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                  <option value="critica">Crítica</option>
                </select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6 text-sm">
              <div>
                <span className="font-medium text-gray-700">Ubicación:</span>
                <p className="text-gray-600">{incident.location || 'No especificada'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Responsable:</span>
                <p className="text-gray-600">{incident.responsible || 'No asignado'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Categoría:</span>
                <p className="text-gray-600">{incident.category || 'Sin categoría'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Código:</span>
                <p className="text-gray-600">{incident.barcode || 'N/A'}</p>
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div>
            <h4 className="font-medium text-gray-800 mb-3">Comentarios</h4>
            
            {/* Add Comment */}
            <div className="flex space-x-3 mb-4">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Agregar comentario..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
              />
              <button
                onClick={handleAddComment}
                disabled={!newComment.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg transition-colors"
              >
                <SafeIcon icon={FiMessageCircle} />
              </button>
            </div>

            {/* Comments List */}
            <div className="space-y-3">
              {(incident.comments || []).map(comment => (
                <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-800">{comment.text}</p>
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                    <span>{comment.author}</span>
                    <span>{format(new Date(comment.createdAt), 'dd/MM/yyyy HH:mm', { locale: es })}</span>
                  </div>
                </div>
              ))}
              {(!incident.comments || incident.comments.length === 0) && (
                <p className="text-gray-500 text-center py-4">No hay comentarios</p>
              )}
            </div>
          </div>

          {/* Timestamps */}
          <div className="border-t border-gray-200 pt-4 text-sm text-gray-500">
            <p>Creado: {format(new Date(incident.createdAt), 'dd/MM/yyyy HH:mm', { locale: es })}</p>
            <p>Actualizado: {format(new Date(incident.updatedAt), 'dd/MM/yyyy HH:mm', { locale: es })}</p>
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setIsEditing(false)}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiSave} />
                <span>Guardar</span>
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-sm"
            onClick={e => e.stopPropagation()}
          >
            <h4 className="text-lg font-semibold text-gray-800 mb-4">¿Eliminar incidencia?</h4>
            <p className="text-gray-600 mb-6">Esta acción no se puede deshacer.</p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Eliminar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default IncidentDetailModal;